import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Search, X, RefreshCw, Zap, Wand2, Radar, LinkIcon, LogOut, ArrowUpRight,
  ChevronDown, Loader2, Send, AlertCircle,
} from "lucide-react";

// The full 12 dimensions, in the order they're rendered on cards + drawer.
const DIMENSIONS = [
  "machiavellian", "urgency", "motivation", "vanity",
  "tech_openness", "reachability", "price_sensitivity", "imposter_risk",
  "loss_aversion", "novelty_seeking", "remote_focus", "gamification_receptivity",
];

const HOOK_OPTIONS = ["", "unfair_advantage", "safety", "novelty", "achievement", "belonging"];
const STATUS_OPTIONS = ["", "new", "queued", "contacted", "replied", "signed", "rejected", "bounced"];

const cx = (...c) => c.filter(Boolean).join(" ");

/* -------------------------------------------------------------------------- */
/* Connect panel — one-time login to the main Chateaux app                    */
/* -------------------------------------------------------------------------- */
const ConnectPanel = ({ onConnected }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/chateaux/login", { email, password });
      toast.success(`Connected as ${data.email}${data.is_founder ? " (founder)" : ""}`);
      onConnected();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md" data-testid="dossier-connect-panel">
      <p className="text-xs uppercase tracking-[0.15em] text-coral mb-3">Connect Chateaux</p>
      <h3 className="font-serif text-3xl text-ink mb-2">Sign in once, then work.</h3>
      <p className="text-sm text-ink/60 mb-8">
        Your bearer token is stored on the server and used to reach <code className="text-ink/70">/api/founder/prospects</code>.
        If it ever expires, you'll be prompted here again.
      </p>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Email</label>
          <input
            type="email" required autoComplete="username"
            value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="dossier-connect-email"
            className="w-full bg-transparent border border-ink/20 px-4 py-3 text-ink focus:border-coral focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Password</label>
          <input
            type="password" required autoComplete="current-password"
            value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="dossier-connect-password"
            className="w-full bg-transparent border border-ink/20 px-4 py-3 text-ink focus:border-coral focus:outline-none"
          />
        </div>
        <button
          type="submit" disabled={busy}
          data-testid="dossier-connect-submit"
          className="inline-flex items-center gap-2 bg-coral text-bone px-5 py-3 hover:bg-coral/90 disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
          Connect
        </button>
      </form>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Score bar — 0-10 with an oxblood dot                                       */
/* -------------------------------------------------------------------------- */
const ScoreBar = ({ score, compact = false }) => {
  const s = Math.max(0, Math.min(10, Number(score) || 0));
  return (
    <div className={cx("relative w-full bg-ink/10", compact ? "h-[2px]" : "h-[3px]")}>
      <div
        className="absolute top-1/2 w-2 h-2 rounded-full bg-coral"
        style={{ left: `${s * 10}%`, transform: "translate(-50%,-50%)" }}
      />
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Prospect row                                                               */
/* -------------------------------------------------------------------------- */
const ProspectRow = ({ p, onOpen }) => {
  const scores = p?.psychographic?.scores || {};
  const hook = p?.psychographic?.dominant_hook || "—";
  const top3 = DIMENSIONS
    .map((k) => ({ k, v: Number(scores[k] || 0) }))
    .sort((a, b) => b.v - a.v).slice(0, 3);
  return (
    <button
      onClick={() => onOpen(p)}
      data-testid={`prospect-row-${p.prospect_id}`}
      className="group w-full text-left grid grid-cols-[1.5fr_1fr_1fr_1.4fr_auto] gap-4 items-center px-4 py-4 border-b border-ink/10 hover:bg-ink/[0.02]"
    >
      <div className="min-w-0">
        <div className="font-serif text-lg text-ink truncate">{p.name || "—"}</div>
        <div className="text-xs text-ink/50 truncate">
          {(p.brokerage || "—")} · {(p.city || "—")}{p.state ? `, ${p.state}` : ""}
        </div>
      </div>
      <div className="text-xs text-ink/60 uppercase tracking-[0.12em]">{p.source || "—"}</div>
      <div className="text-xs font-mono">
        <span className="inline-block px-2 py-0.5 border border-coral/40 text-coral">
          {hook}
        </span>
      </div>
      <div className="space-y-1.5">
        {top3.map((d) => (
          <div key={d.k} className="flex items-center gap-2">
            <span className="w-32 text-[10px] uppercase tracking-[0.1em] text-ink/60 truncate">{d.k}</span>
            <div className="flex-1"><ScoreBar score={d.v} compact /></div>
            <span className="w-6 text-right text-xs font-mono text-ink/60">{d.v}</span>
          </div>
        ))}
      </div>
      <ArrowUpRight className="w-4 h-4 text-ink/30 group-hover:text-coral" />
    </button>
  );
};

/* -------------------------------------------------------------------------- */
/* Prospect detail drawer                                                     */
/* -------------------------------------------------------------------------- */
const DetailDrawer = ({ prospect, onClose, onChanged }) => {
  const [busy, setBusy] = useState(null);
  const [status, setStatus] = useState(prospect?.status || "new");
  const [notes, setNotes] = useState("");

  if (!prospect) return null;
  const scores = prospect?.psychographic?.scores || {};
  const reasoning = prospect?.psychographic?.reasoning;
  const hook = prospect?.psychographic?.dominant_hook;
  const outreach = prospect?.outreach_hook;

  const run = async (label, fn) => {
    setBusy(label);
    try { await fn(); toast.success(`${label} done`); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || `${label} failed`); }
    finally { setBusy(null); }
  };

  const setStatusRemote = async () => {
    await api.post(`/admin/dossier/prospects/${prospect.prospect_id}/status`, { status, notes: notes || undefined });
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="prospect-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full max-w-2xl bg-bone overflow-y-auto border-l border-ink/10">
        <div className="sticky top-0 bg-bone border-b border-ink/10 px-8 py-5 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.15em] text-coral mb-1">Prospect</p>
            <h3 className="font-serif text-2xl text-ink truncate">{prospect.name || "—"}</h3>
            <p className="text-xs text-ink/50 truncate">
              {(prospect.brokerage || "—")} · {prospect.city}{prospect.state ? `, ${prospect.state}` : ""} · {prospect.source || "—"}
            </p>
          </div>
          <button onClick={onClose} data-testid="drawer-close" className="p-2 hover:bg-ink/5">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-8 py-6 space-y-8">
          {/* Scores */}
          <section>
            <div className="text-xs uppercase tracking-[0.15em] text-ink/60 mb-4">12 dimensions</div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {DIMENSIONS.map((k) => (
                <div key={k}>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <span className="text-[11px] uppercase tracking-[0.1em] text-ink/70">{k}</span>
                    <span className="text-xs font-mono text-ink/60">{Number(scores[k] || 0)}</span>
                  </div>
                  <ScoreBar score={scores[k]} />
                </div>
              ))}
            </div>
          </section>

          {/* Hook + reasoning */}
          {(hook || reasoning) && (
            <section className="bg-ink/[0.03] border-l-2 border-coral p-5">
              <div className="text-xs uppercase tracking-[0.15em] text-coral mb-2">Hook</div>
              <div className="font-mono text-sm text-ink mb-3">{hook || "—"}</div>
              {reasoning && (
                <p className="text-sm text-ink/70 leading-relaxed italic">{reasoning}</p>
              )}
            </section>
          )}

          {/* Outreach preview */}
          {outreach && (outreach.subject || outreach.body) && (
            <section>
              <div className="text-xs uppercase tracking-[0.15em] text-ink/60 mb-3">Outreach hook</div>
              <div className="border border-ink/15 p-5 space-y-3">
                {outreach.subject && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.1em] text-ink/50 mb-1">Subject</div>
                    <div className="text-sm text-ink">{outreach.subject}</div>
                  </div>
                )}
                {outreach.body && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.1em] text-ink/50 mb-1">Body</div>
                    <pre className="text-sm text-ink/80 whitespace-pre-wrap font-sans">{outreach.body}</pre>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Contact */}
          {(prospect.email || prospect.phone || prospect.listing_url) && (
            <section>
              <div className="text-xs uppercase tracking-[0.15em] text-ink/60 mb-3">Contact</div>
              <div className="text-sm text-ink/80 space-y-1">
                {prospect.email && <div>📧 <a href={`mailto:${prospect.email}`} className="underline">{prospect.email}</a></div>}
                {prospect.phone && <div>📞 {prospect.phone}</div>}
                {prospect.listing_url && (
                  <div>🔗 <a href={prospect.listing_url} target="_blank" rel="noopener noreferrer" className="underline">{prospect.listing_url}</a></div>
                )}
              </div>
            </section>
          )}

          {/* Status + notes */}
          <section>
            <div className="text-xs uppercase tracking-[0.15em] text-ink/60 mb-3">Status</div>
            <div className="flex gap-3 items-center mb-3">
              <select
                value={status} onChange={(e) => setStatus(e.target.value)}
                data-testid="drawer-status-select"
                className="bg-transparent border border-ink/20 px-3 py-2 text-sm"
              >
                {STATUS_OPTIONS.filter(Boolean).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button
                onClick={() => run("Save status", setStatusRemote)}
                disabled={busy === "Save status"}
                data-testid="drawer-status-save"
                className="inline-flex items-center gap-2 bg-ink text-bone px-4 py-2 text-sm hover:bg-ink/90"
              >
                {busy === "Save status" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                Save
              </button>
            </div>
            <textarea
              value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optional) — saved with status change"
              className="w-full bg-transparent border border-ink/15 px-3 py-2 text-sm min-h-[64px]"
            />
          </section>

          {/* Actions */}
          <section>
            <div className="text-xs uppercase tracking-[0.15em] text-ink/60 mb-3">Actions</div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => run("Enrich", () => api.post(`/admin/dossier/prospects/${prospect.prospect_id}/enrich`))}
                disabled={!!busy} data-testid="drawer-enrich"
                className="flex items-center justify-center gap-2 border border-ink/20 px-4 py-3 text-sm hover:bg-ink hover:text-bone"
              >
                {busy === "Enrich" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Enrich
              </button>
              <button
                onClick={() => run("Generate hook", () => api.post(`/admin/dossier/prospects/${prospect.prospect_id}/generate-hook`))}
                disabled={!!busy} data-testid="drawer-hook"
                className="flex items-center justify-center gap-2 border border-ink/20 px-4 py-3 text-sm hover:bg-ink hover:text-bone"
              >
                {busy === "Generate hook" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                Generate hook
              </button>
              <button
                onClick={() => run("Push to Smartlead", () => api.post(`/admin/dossier/prospects/push-to-smartlead`, { prospect_ids: [prospect.prospect_id] }))}
                disabled={!!busy} data-testid="drawer-smartlead"
                className="col-span-2 flex items-center justify-center gap-2 bg-coral text-bone px-4 py-3 text-sm hover:bg-coral/90"
              >
                {busy === "Push to Smartlead" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Push to Smartlead
              </button>
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Scrape modal — add prospects for a new market                              */
/* -------------------------------------------------------------------------- */
const ScrapeModal = ({ open, onClose, onDone }) => {
  const [location, setLocation] = useState("");
  const [state, setState] = useState("");
  const [count, setCount] = useState(20);
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/dossier/prospects/scrape", { location, state, count });
      toast.success(`Scrape queued${data?.job_id ? ` — job ${data.job_id}` : ""}`);
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Scrape failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="scrape-modal">
      <div className="bg-bone max-w-md w-full border border-ink/10 p-8">
        <div className="flex justify-between items-start mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-coral mb-1">Scrape a new market</p>
            <h3 className="font-serif text-2xl">Add prospects</h3>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">City</label>
            <input value={location} onChange={(e) => setLocation(e.target.value)} required
              className="w-full bg-transparent border border-ink/20 px-3 py-2" placeholder="Frisco" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">State</label>
            <input value={state} onChange={(e) => setState(e.target.value.toUpperCase())} maxLength={2}
              className="w-full bg-transparent border border-ink/20 px-3 py-2" placeholder="TX" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-ink/60 mb-2">Target count</label>
            <input type="number" value={count} onChange={(e) => setCount(Number(e.target.value))}
              min={1} max={200}
              className="w-full bg-transparent border border-ink/20 px-3 py-2" />
          </div>
          <button
            type="submit" disabled={busy} data-testid="scrape-submit"
            className="inline-flex items-center gap-2 bg-coral text-bone px-5 py-3 hover:bg-coral/90 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />}
            Start scrape
          </button>
        </form>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Main tab                                                                   */
/* -------------------------------------------------------------------------- */
export const DossierTab = () => {
  const [connection, setConnection] = useState(null); // { connected, email, is_founder }
  const [loading, setLoading] = useState(true);
  const [prospects, setProspects] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(null);
  const [scrapeOpen, setScrapeOpen] = useState(false);

  const [filters, setFilters] = useState({
    location: "", state: "", source: "", hook: "", status: "",
    min_score: 0, sort_by: "machiavellian", order: "desc", limit: 50,
  });

  const checkConnection = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/chateaux/status");
      setConnection(data);
      return data.connected;
    } catch {
      setConnection({ connected: false });
      return false;
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== undefined)
      );
      const [{ data }, sum] = await Promise.all([
        api.get("/admin/dossier/prospects", { params }),
        api.get("/admin/dossier/summary").then((r) => r.data).catch(() => null),
      ]);
      setProspects(data.prospects || []);
      setTotal(data.total || 0);
      setSummary(sum);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 428) {
        setConnection({ connected: false });
        toast.error("Chateaux session expired — please reconnect");
      } else {
        toast.error(err?.response?.data?.detail || "Failed to load prospects");
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { checkConnection().then((ok) => { if (ok) load(); else setLoading(false); }); }, [checkConnection]); // eslint-disable-line
  useEffect(() => { if (connection?.connected) load(); }, [filters]); // eslint-disable-line

  const disconnect = async () => {
    await api.delete("/admin/chateaux/login");
    setConnection({ connected: false });
    setProspects([]); setSummary(null);
    toast.success("Disconnected");
  };

  const resweep = async () => {
    try {
      const { data } = await api.post("/admin/dossier/prospects/resweep", { limit: 30, since_days: 7 });
      toast.success(`Resweep: checked ${data.checked}, spikes ${data.spiked_prospects}`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Resweep failed"); }
  };

  /* --- render ------------------------------------------------------------- */
  if (loading && !connection) {
    return <div className="text-ink/50">Loading…</div>;
  }

  if (!connection?.connected) {
    return (
      <div className="space-y-6" data-testid="dossier-tab">
        <div className="flex items-start gap-3 border border-ink/10 bg-ink/[0.03] p-4 text-sm text-ink/70">
          <AlertCircle className="w-4 h-4 text-coral shrink-0 mt-0.5" />
          <div>
            The founder API lives on <code className="text-ink/80">api.chateaux.ai</code> and needs a real user session.
            Sign in once with your Chateaux account — we'll store the bearer server-side and refresh only when it expires.
          </div>
        </div>
        <ConnectPanel onConnected={() => checkConnection().then((ok) => { if (ok) load(); })} />
      </div>
    );
  }

  return (
    <div data-testid="dossier-tab">
      {/* Header + connection */}
      <div className="flex items-center justify-between mb-6 pb-6 border-b border-ink/10">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-coral mb-1">Dossier</p>
          <h2 className="font-serif text-3xl text-ink">Prospect Intelligence</h2>
          <p className="text-sm text-ink/50 mt-1">
            Connected as <span className="text-ink/70">{connection.email}</span>
            {connection.is_founder && <span className="ml-2 text-coral">· founder</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setScrapeOpen(true)} data-testid="dossier-scrape-open"
            className="inline-flex items-center gap-2 bg-coral text-bone px-4 py-2 text-sm hover:bg-coral/90"
          >
            <Radar className="w-4 h-4" /> Scrape market
          </button>
          <button
            onClick={resweep} data-testid="dossier-resweep"
            className="inline-flex items-center gap-2 border border-ink/20 px-4 py-2 text-sm hover:bg-ink/5"
          >
            <RefreshCw className="w-4 h-4" /> Resweep
          </button>
          <button
            onClick={disconnect} data-testid="dossier-disconnect"
            className="inline-flex items-center gap-2 border border-ink/20 px-3 py-2 text-sm text-ink/70 hover:bg-ink/5"
          >
            <LogOut className="w-4 h-4" /> Disconnect
          </button>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="border border-ink/10 p-4">
            <div className="text-xs uppercase tracking-[0.12em] text-ink/50 mb-1">Total</div>
            <div className="font-serif text-3xl text-ink">{summary.total || 0}</div>
          </div>
          {["unfair_advantage", "safety", "novelty"].map((h) => (
            <div key={h} className="border border-ink/10 p-4">
              <div className="text-xs uppercase tracking-[0.12em] text-ink/50 mb-1">{h}</div>
              <div className="font-serif text-3xl text-ink">{summary.by_hook?.[h] || 0}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4 items-end">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.12em] text-ink/50 mb-1">City</label>
          <input value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })}
            data-testid="filter-city"
            className="w-full bg-transparent border border-ink/15 px-2 py-1.5 text-sm" placeholder="Frisco" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.12em] text-ink/50 mb-1">State</label>
          <input value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value.toUpperCase() })}
            maxLength={2} data-testid="filter-state"
            className="w-full bg-transparent border border-ink/15 px-2 py-1.5 text-sm" placeholder="TX" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.12em] text-ink/50 mb-1">Source</label>
          <input value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}
            data-testid="filter-source"
            className="w-full bg-transparent border border-ink/15 px-2 py-1.5 text-sm" placeholder="linkedin" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.12em] text-ink/50 mb-1">Hook</label>
          <select value={filters.hook} onChange={(e) => setFilters({ ...filters, hook: e.target.value })}
            data-testid="filter-hook"
            className="w-full bg-transparent border border-ink/15 px-2 py-1.5 text-sm">
            {HOOK_OPTIONS.map((h) => <option key={h} value={h}>{h || "any"}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.12em] text-ink/50 mb-1">Sort by</label>
          <select value={filters.sort_by} onChange={(e) => setFilters({ ...filters, sort_by: e.target.value })}
            data-testid="filter-sort"
            className="w-full bg-transparent border border-ink/15 px-2 py-1.5 text-sm">
            {DIMENSIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.12em] text-ink/50 mb-1">Min score</label>
          <input type="number" min={0} max={10}
            value={filters.min_score}
            onChange={(e) => setFilters({ ...filters, min_score: Number(e.target.value) })}
            data-testid="filter-min-score"
            className="w-full bg-transparent border border-ink/15 px-2 py-1.5 text-sm" />
        </div>
      </div>

      {/* List */}
      <div className="border border-ink/10">
        <div className="px-4 py-3 border-b border-ink/10 flex items-center justify-between bg-ink/[0.02]">
          <div className="text-xs uppercase tracking-[0.12em] text-ink/60">
            {loading ? "Loading…" : `${prospects.length} of ${total} prospects`}
          </div>
          <button onClick={load} data-testid="reload-list" className="text-xs text-ink/60 hover:text-ink flex items-center gap-1">
            <RefreshCw className={cx("w-3 h-3", loading && "animate-spin")} /> Reload
          </button>
        </div>
        {prospects.length === 0 && !loading ? (
          <div className="p-12 text-center text-ink/50">
            No prospects matched. Try scraping a market to populate.
          </div>
        ) : (
          <div>
            {prospects.map((p) => (
              <ProspectRow key={p.prospect_id || p.id} p={p} onOpen={setSelected} />
            ))}
          </div>
        )}
      </div>

      <DetailDrawer prospect={selected} onClose={() => setSelected(null)} onChanged={load} />
      <ScrapeModal open={scrapeOpen} onClose={() => setScrapeOpen(false)} onDone={load} />
    </div>
  );
};
